import {
  createStore,
  createEffect,
  createDomain,
  combine,
  sample,
  clearNode,
  Store,
  launch,
  step,
  Step
} from '@effector/effector'
import * as effector from '@effector/effector'
import {
  generateEventDefinition,
  generateEffectDefinition,
  generateStoreDefinition,
  generateWatchDefinition,
  generateSampleDefinition,
  generateOnDefinition,
  generateForwardDefinition
} from '../highlight/generateDefinition'
import {exec} from '../runtime/exec'
import {workingSet, Node, Edge, runRuntime} from '../workingSet'
import {graphParser} from '../runtime/graphParser'
import {defaultCode} from '../runtime/defaultCode'
import {
  stores,
  independentEvents,
  effects,
  watches,
  ons,
  forwards,
  samples
} from '../runtime/units'
import {runtimeRoot} from '../runtime/runtimeID'
import {addPendingEffect, removePendingEffect} from '../runtime/activeEffects'
import {updateStackReceiver} from '../runtime/unitUpdate'
import {consoleApi} from '../runtime/console'
import * as DOM from '../h'

const runtimeFX = createEffect({
  async handler(code) {
    const root = createDomain()
    let parsed: {
      nodes: Node[]
      edges: Edge[]
    }
    const implicitStack = []
    const addStackReceiver = (node: Step, prepend: boolean = false) => {
      //@ts-ignore
      node.meta.runtimeID = root.id
      updateStackReceiver(node, implicitStack, prepend)
    }
    const globals = {
      ...effector,
      ...DOM,
      createEvent: root.event,
      createEffect: root.effect,
      createStore: root.store,
      createDomain: root.domain,
      console: consoleApi,
      initialized() {
        parsed = graphParser(root, {
          store({node}) {
            addStackReceiver(node)
          },
          effect({node}) {
            handleEffect(node)
            addStackReceiver(node)
          },
          event({node}) {
            addStackReceiver(node)
          },
          watch({node}) {
            const {fn} = node.seq[0].data
            node.seq[0].data.fn = (upd, scope, stack) => {
              implicitStack.push(stack)
              try {
                fn(upd, scope, stack)
              } finally {
                implicitStack.pop()
              }
              return upd
            }
            addStackReceiver(node, true)
          },
          forward({node}) {
            addStackReceiver(node)
          }
        })
      }
    }
    function handleEffect(fx) {
      const {getHandler} = fx.scope.runner.scope
      //@ts-ignore
      fx.scope.runner.scope.getHandler = () => {
        const handler = getHandler()
        return params =>
          new Promise(async (rs, rj) => {
            const callID = ++nextCallID
            launch(
              addPendingEffect,
              {
                name: fx.meta.name,
                callID,
                params,
                //@ts-ignore
                runtimeID: root.id
              },
              //@ts-ignore
              true
            )
            try {
              const result = await handler(params)
              launch(
                removePendingEffect,
                {
                  callID,
                  status: 'done',
                  value: result,
                  //@ts-ignore
                  runtimeID: root.id,
                  cb: rs
                },
                //@ts-ignore
                true
              )
            } catch (err) {
              launch(
                removePendingEffect,
                {
                  callID,
                  status: 'fail',
                  value: err,
                  //@ts-ignore
                  runtimeID: root.id,
                  cb: rj
                },
                //@ts-ignore
                true
              )
            }
          })
      }
      fx.scope.runner.scope.getHandler.original = getHandler
    }
    const error = await exec(code, globals)
    return {error, root, parsed}
  }
})
workingSet.on(runtimeFX.done, (_, {result}) => result.parsed)
runtimeRoot.on(runtimeFX.done, (oldRoot, {result: {root}}) => {
  clearNode(oldRoot)
  return root
})

export const runtimeError = createStore(null)
  .on(runtimeFX.done, (_, {result: {error}}) => error)
  .on(runtimeFX.fail, (_, {error}) => error)

let nextCallID = 0

function createDefinition<T>(list: Store<T[]>, generator) {
  return list.map(list =>
    list.reduce((acc, data) => [...acc, generator(data)], [])
  )
}
const storesCode = createDefinition(stores, generateStoreDefinition)
const eventsCode = createDefinition(independentEvents, generateEventDefinition)
const effectsCode = createDefinition(effects, generateEffectDefinition)
const watchesCode = createDefinition(watches, generateWatchDefinition)
const onCode = createDefinition(ons, generateOnDefinition)
const forwardCode = createDefinition(forwards, generateForwardDefinition)
const samplesCode = createDefinition(samples, generateSampleDefinition)

const initRuntimeCode = createStore(`
initialized()
await timerFX(cycle.getState())
`)
export const plainCode = combine(
  storesCode,
  eventsCode,
  effectsCode,
  watchesCode,
  samplesCode,
  onCode,
  forwardCode,
  initRuntimeCode,
  (stores, events, effects, watches, samples, ons, forwards, init) =>
    [
      stores.join(''),
      events.join(''),
      effects.join(''),
      samples.join(''),
      ons.join(''),
      forwards.join(''),
      watches.join(''),
      init
    ].join(`\n`)
)

sample({
  source: plainCode,
  clock: runRuntime,
  target: runtimeFX
})

runtimeFX(defaultCode)
